import * as React from "react";
import { useCallback, useState } from "react";
import { Button, styled, Tooltip } from "@mui/material";
import clsx from "clsx";
import { UsePatientSummaryApi } from "fe-common/patientSummary/useFetchPatientSummary";
import { ReactComponent as ChartIcon } from "../assets/patientCard/chartViewIcon.svg";
import { ReactComponent as SummaryIcon } from "../assets/patientCard/summaryViewIcon.svg";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { ReactComponent as CopyIcon } from "../assets/copyIcon.svg";
import EditIcon from "@mui/icons-material/Edit";
import { scrollableStyle } from "fe-common/ui/commonStyles";
import { useTranslateByGender } from "fe-common/locale/useTranslateByGender";
import { CommonPatientCardChart } from "./commonPatientCardChart";
import {
  PATIENT_CARD_HEADER_DESKTOP_HEIGHT,
  PatientCardSummaryAction,
} from "./patientCardCommonUi";
import { ViewOnAgent } from "./viewOnAgent";
import { PatientFirstSummaryView } from "./patientFirstSummaryView";
import { QualifiedFeature } from "fe-common/graphql";
import Toast from "../layout/toast";
import { KAHUN_PLATFORM } from "@kahunmedical/user-management";
import { CSSCollapseTransitionComponent } from "./layout/CSSCollapseTransitionComponent";

//region [[ Styles ]]
const PatientCardSummaryView = styled("div")(({ theme }) => ({
  flex: 2,
  [theme.breakpoints.down("xs")]: {
    padding: 0,
    margin: 0,
  },
}));

const SummaryActionsBar = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  flexWrap: "wrap-reverse",
});

const ChartOrSummaryButton = styled(Button)({
  minWidth: "auto",
  width: "37px",
  height: "37px",
  padding: "10px",
  borderRadius: "6px",
  marginRight: "9px",
  background: "#E6E4FF",
  "& svg": {
    "& [fill]": { fill: "#5A52C3" },
    "& [stroke]": { stroke: "#5A52C3" },
  },
  "&.selected": {
    background: "#5A52C3",
    "& svg": {
      "& [fill]": { fill: "white" },
      "& [stroke]": { stroke: "white" },
    },
  },
});

const ToggleIcons = styled("div")({
  display: "flex",
  flexFlow: "row",
  justifyContent: "flex-end",
});

const PatientCardActionContainer = styled("div")({
  display: "flex",
  flexFlow: "row-reverse",
  alignItems: "center",
  flexWrap: "wrap-reverse",
  flexBasis: "fit-content",
  flexGrow: 1,
});

const PatientCardSummaryContent = styled(PatientFirstSummaryView)(
  ({ theme }) => ({
    "& .summary-content": {
      margin: "8px -15px",
      padding: "5px 15px",
      maxHeight: `calc(100vh - ${PATIENT_CARD_HEADER_DESKTOP_HEIGHT + 150}px)`,
      ...scrollableStyle,
      [theme.breakpoints.down("md")]: {
        maxHeight: "fit-content",
        overflow: "initial",
      },
    },
  }),
);
//endregion [[ Styles ]]

//region [[ Props ]]

export enum PatientCardCommonActions {
  COPY_TO_CLIPBOARD,
  VIEW_IN_AGENT,
  NONE,
}

export interface PatientCardSummaryProps {
  className?: string;
  patientSummary: UsePatientSummaryApi;
  caseId: string;
  clinicName?: string;
  clinicId?: string;
  supportedSections: string[];
  inExtension?: boolean;
  actions?: PatientCardCommonActions[];
  inlineChiefComplaint?: boolean;
  patientCase?: {
    main: QualifiedFeature[];
    clinicalFeatures: QualifiedFeature[];
    reviewOfSystems: QualifiedFeature[];
    background: QualifiedFeature[];
  };
  children?: React.ReactNode;
  onCopy?: (copiedContent: string) => void;
  platform: KAHUN_PLATFORM;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

//region [[Texts]]
const SUMMARY_TITLE = "Summary of current illness";

const CHART_TITLE = "Patient chart";

const SWITCH_TO_CHART = "Switch to chart view";

const BACK_TO_SUMMARY = "Back to text";

const COPIED_INVITATION_TEXT = "COPIED";

const EDIT_TEXT = "EDIT";

const AGENT_LINK_TOOLTIP_TEXT =
  "Patient assessment is a read-only view. For an editable chart, click here. Note, changes made in the editable chart will affect the original patient assessment.";

//endregion [[Texts]]
const SUMMARY_CONTAINER_ID = "summary-text-to-copy";

export const PatientCardSummaryOrChartToggle = ({
  patientSummary,
  inExtension,
  clinicName,
  clinicId,
  caseId,
  ...props
}: PatientCardSummaryProps) => {
  const { translate } = useTranslateByGender();
  const [showChartView, setShowChartView] = useState<boolean>(false);

  const toggleChartAndSummary = useCallback(
    () => setShowChartView((oldValue) => !oldValue),
    [setShowChartView],
  );

  const [copyLabel, setCopyLabel] = useState(null);

  const copyToClipboard = useCallback(() => {
    evt({
      category: CATEGORY.PATIENT_CARD,
      action: ACTION.COPY_EXPORT_DATA_CLICKED,
      props: {
        caseId,
        clinicName,
        platform: props.platform,
        clinicId,
      },
    });

    const text = document.getElementById(SUMMARY_CONTAINER_ID);

    let range, selection;
    selection = window.getSelection();
    range = document.createRange();

    range.selectNodeContents(text);
    selection.removeAllRanges();
    selection.addRange(range);

    document.execCommand("copy");

    window.getSelection().removeAllRanges();

    setCopyLabel(translate(COPIED_INVITATION_TEXT));

    if (props.onCopy) {
      props.onCopy(window?.getSelection()?.toString());
    }
  }, [translate]);

  return (
    <PatientCardSummaryView
      className={clsx("patient-card-summary", props.className)}
    >
      <SummaryActionsBar className={"summary-actions-bar"}>
        {props.children}
        {props.patientCase && (
          <ToggleIcons>
            <Tooltip
              TransitionComponent={
                inExtension ? CSSCollapseTransitionComponent : undefined
              }
              title={showChartView ? BACK_TO_SUMMARY : SUMMARY_TITLE}
            >
              <ChartOrSummaryButton
                onClick={toggleChartAndSummary}
                className={showChartView ? "" : "selected"}
              >
                <SummaryIcon />
              </ChartOrSummaryButton>
            </Tooltip>
            <Tooltip
              TransitionComponent={
                inExtension ? CSSCollapseTransitionComponent : undefined
              }
              title={showChartView ? CHART_TITLE : SWITCH_TO_CHART}
            >
              <ChartOrSummaryButton
                onClick={toggleChartAndSummary}
                className={showChartView ? "selected" : ""}
              >
                <ChartIcon />
              </ChartOrSummaryButton>
            </Tooltip>
          </ToggleIcons>
        )}
        <PatientCardActionContainer className={"actions-container"}>
          {(!props.actions?.length ||
            props.actions.includes(
              PatientCardCommonActions.COPY_TO_CLIPBOARD,
            )) && (
            <PatientCardSummaryAction onClick={copyToClipboard}>
              <CopyIcon />
            </PatientCardSummaryAction>
          )}
          {(!props.actions?.length ||
            props.actions.includes(PatientCardCommonActions.VIEW_IN_AGENT)) && (
            <ViewOnAgent
              caseId={caseId}
              label={translate(EDIT_TEXT)}
              icon={<EditIcon />}
              tooltip={inExtension ? null : AGENT_LINK_TOOLTIP_TEXT}
            />
          )}
        </PatientCardActionContainer>
      </SummaryActionsBar>
      {!showChartView && (
        <PatientCardSummaryContent
          id={SUMMARY_CONTAINER_ID}
          patientSummary={patientSummary.summary}
          showPatientInformation={false}
          supportedSections={props.supportedSections}
          hiddenSections={["assessment"]}
          onNotify={(text) => setCopyLabel(text)}
          platform={props.platform}
          clinicName={clinicName}
          clinicId={clinicId}
          disableTooltip={inExtension}
        />
      )}
      {showChartView && (
        <CommonPatientCardChart
          patientCase={props.patientCase}
          id={SUMMARY_CONTAINER_ID}
        />
      )}
      <Toast
        position={"absolute"}
        text={copyLabel}
        open={!!copyLabel}
        onClose={() => setCopyLabel(null)}
      />
    </PatientCardSummaryView>
  );
};
